import { trpc } from "@/api/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FormError } from "@/shared/components/form-error";
import { TurnstileWidget, turnstileRequired } from "@/shared/components/turnstile-widget";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  type SubmitKontaktFormValues,
  type SubmitKontaktInput,
  submitKontaktSchema,
} from "@template/shared";
import { CheckCircle2 } from "lucide-react";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";

interface KontaktFormProps {
  onSubmitted?: () => void;
  /** Předvyplněný text do pole zprávy — typicky kontext z místa odkud uživatel přišel. */
  initialMessage?: string;
}

export function KontaktForm({ onSubmitted, initialMessage }: KontaktFormProps) {
  // Mount time — server odmítne submissions rychlejší než 2 s (anti-bot).
  const renderedAtRef = useRef<number>(Date.now());
  const [submitted, setSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>(undefined);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SubmitKontaktFormValues, unknown, SubmitKontaktInput>({
    resolver: zodResolver(submitKontaktSchema),
    defaultValues: {
      email: "",
      jmeno: "",
      telefon: "",
      zprava: initialMessage ?? "",
      honeypot: "",
      renderedAt: renderedAtRef.current,
    },
  });

  const mutation = trpc.hca.kontakt.submit.useMutation({
    onSuccess: () => {
      setSubmitted(true);
      setTurnstileToken(undefined);
      reset({
        email: "",
        jmeno: "",
        telefon: "",
        zprava: "",
        honeypot: "",
        // Reset razítka, kdyby uživatel chtěl poslat další zprávu.
        renderedAt: Date.now(),
      });
      onSubmitted?.();
    },
  });

  const onSubmit = handleSubmit((data) =>
    mutation.mutate({ ...data, ...(turnstileToken ? { turnstileToken } : {}) }),
  );

  if (submitted) {
    return (
      <output
        className="flex flex-col items-center gap-3 rounded-lg border border-border bg-muted/30 p-6 text-center"
        data-testid="hca-kontakt-success"
      >
        <CheckCircle2 className="h-10 w-10 text-primary" aria-hidden />
        <div className="space-y-1">
          <h3 className="font-semibold text-lg">Děkujeme za zprávu</h3>
          <p className="text-sm text-muted-foreground">
            Ozveme se vám do 24 hodin na uvedený e-mail.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setSubmitted(false)}
          className="text-primary text-sm underline-offset-4 hover:underline"
          data-testid="hca-kontakt-send-another"
        >
          Poslat další zprávu
        </button>
      </output>
    );
  }

  const isLoading = mutation.isPending;

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="hca-kontakt-form" noValidate>
      <FormError message={mutation.error?.message} testId="hca-kontakt-error" />

      {/*
			  Honeypot — skryté pole pro boty. Lidé ho nevidí ani s focusem
			  (tabindex=-1, aria-hidden, off-screen). Pokud něco vyplní, server
			  silently zahodí submit.
			*/}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-[-9999px] top-auto h-0 w-0 overflow-hidden"
      >
        <label htmlFor="hca-kontakt-website">Website (nevyplňujte)</label>
        <input
          id="hca-kontakt-website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          {...register("honeypot")}
        />
      </div>
      <input type="hidden" {...register("renderedAt", { valueAsNumber: true })} />

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="kontakt-jmeno">Jméno</Label>
          <Input
            id="kontakt-jmeno"
            autoComplete="name"
            aria-describedby={errors.jmeno ? "kontakt-jmeno-error" : undefined}
            data-testid="hca-kontakt-jmeno"
            {...register("jmeno")}
          />
          {errors.jmeno && (
            <p id="kontakt-jmeno-error" role="alert" className="text-destructive text-sm">
              {errors.jmeno.message}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="kontakt-telefon">Telefon</Label>
          <Input
            id="kontakt-telefon"
            type="tel"
            autoComplete="tel"
            placeholder="+420 777 123 456"
            aria-describedby={errors.telefon ? "kontakt-telefon-error" : undefined}
            data-testid="hca-kontakt-telefon"
            {...register("telefon")}
          />
          {errors.telefon && (
            <p id="kontakt-telefon-error" role="alert" className="text-destructive text-sm">
              {errors.telefon.message}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="kontakt-email">
          E-mail <span className="text-destructive">*</span>
        </Label>
        <Input
          id="kontakt-email"
          type="email"
          required
          autoFocus
          autoComplete="email"
          aria-describedby={errors.email ? "kontakt-email-error" : undefined}
          data-testid="hca-kontakt-email"
          {...register("email")}
        />
        {errors.email && (
          <p id="kontakt-email-error" role="alert" className="text-destructive text-sm">
            {errors.email.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="kontakt-zprava">Zpráva</Label>
        <Textarea
          id="kontakt-zprava"
          rows={4}
          placeholder="Napište, jak vám můžeme pomoct…"
          aria-describedby={errors.zprava ? "kontakt-zprava-error" : undefined}
          data-testid="hca-kontakt-zprava"
          {...register("zprava")}
        />
        {errors.zprava && (
          <p id="kontakt-zprava-error" role="alert" className="text-destructive text-sm">
            {errors.zprava.message}
          </p>
        )}
      </div>

      <label
        htmlFor="kontakt-consent"
        className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 text-sm"
      >
        <input
          id="kontakt-consent"
          type="checkbox"
          className="mt-1 h-5 w-5"
          aria-describedby={errors.consent ? "kontakt-consent-error" : undefined}
          data-testid="hca-kontakt-consent"
          {...register("consent")}
        />
        <span>
          Souhlasím se zpracováním e-mailu pro účely vyřízení dotazu. Více v{" "}
          <a href="/gdpr" target="_blank" rel="noreferrer" className="text-primary underline">
            GDPR dokumentu
          </a>
          .
        </span>
      </label>
      {errors.consent && (
        <p id="kontakt-consent-error" role="alert" className="-mt-2 text-destructive text-sm">
          {errors.consent.message}
        </p>
      )}

      <TurnstileWidget onToken={setTurnstileToken} testId="hca-kontakt-turnstile" />

      <Button
        type="submit"
        className="w-full"
        disabled={isLoading || (turnstileRequired && !turnstileToken)}
        data-testid="hca-kontakt-submit"
      >
        {isLoading ? "Odesílám…" : "Odeslat dotaz"}
      </Button>
    </form>
  );
}
